import {
  ArrowDropDown as ArrowDropDownIcon,
  ArrowRight as ArrowRightIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';

import type { DropdownOption, ListSlotProps } from '../types';

export interface VNDropdownOUOptionsProps extends ListSlotProps {
  /**
   * Optional callback fired when the "open in new" link icon is clicked for an item.
   * Stops event propagation so clicking the link does not also select the option.
   * The icon is only rendered when both `link` is provided and `option.isLinkable` is true.
   */
  link?: (option: DropdownOption) => void;
}

/**
 * Custom list slot for Organizational Unit (OU) tree data.
 *
 * Uses `depth` and `childCount` from `DropdownOption` to render an indented, expandable
 * tree. Pass this as `listComponent` on `VNDropdown` / `VNDropdownAutocomplete`:
 *
 * ```tsx
 * <VNDropdown
 *   ...
 *   listComponent={(props) => <VNDropdownOUOptions {...props} link={goToOU} />}
 * />
 * ```
 *
 * Note: the `link` prop cannot be passed through `listComponent` directly (the slot
 * interface only receives `ListSlotProps`), so use a closure / render prop as shown above.
 */
const VNDropdownOUOptions = ({
  options,
  selectedIds,
  onSelect,
  multiple = false,
  focusedIndex,
  listboxId,
  optionIdPrefix,
  link,
}: VNDropdownOUOptionsProps) => {
  const [expandedIds, setExpandedIds] = useState<Set<DropdownOption['id']>>(() => {
    // Pre-expand all root nodes (depth 0) so their immediate children are visible by default
    const roots = options.filter((o) => (o.depth ?? 0) === 0).map((o) => o.id);
    return new Set(roots);
  });

  // Whenever the options array changes (e.g. filtered by search), expand every
  // node with children that is present in the current list. When filtering is active,
  // VNDropdownBase only passes matched nodes + their ancestors, so this ensures
  // those ancestors are always expanded and their matched children are visible.
  useEffect(() => {
    setExpandedIds(() => {
      const toExpand = options.filter((o) => (o.childCount ?? 0) > 0).map((o) => o.id);
      return new Set(toExpand);
    });
  }, [options]);

  const toggleExpand = (id: DropdownOption['id'], e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /**
   * Precompute the set of visible option IDs in a single O(n) forward pass.
   * An option is visible when every ancestor in the depth hierarchy is expanded.
   *
   * depthState[d] tracks whether the most recently seen node at depth d is both
   * visible and expanded, meaning its children may be visible. The array is
   * truncated each time we move to a shallower depth to discard stale
   * sibling-subtree data from the previous branch.
   */
  const visibleIds = useMemo(() => {
    const set = new Set<DropdownOption['id']>();
    const depthState: (boolean | undefined)[] = [];

    for (const option of options) {
      const depth = option.depth ?? 0;

      let visible: boolean;
      if (depth === 0) {
        visible = true;
      } else {
        // Find the nearest ancestor at any smaller depth level.
        visible = false;
        for (let d = depth - 1; d >= 0; d--) {
          if (depthState[d] !== undefined) {
            visible = depthState[d]!;
            break;
          }
        }
      }

      if (visible) set.add(option.id);

      // Discard stale data from deeper sibling subtrees, then record this node.
      depthState.length = depth + 1;
      depthState[depth] = visible && expandedIds.has(option.id);
    }

    return set;
  }, [options, expandedIds]);

  return (
    <ul
      id={listboxId}
      role="listbox"
      aria-multiselectable={multiple || undefined}
      className="vn-dropdown-list vn-dropdown-list--tree"
    >
      {options.map((option, index) => {
        const isSelected = selectedIds.includes(option.id);
        const isFocused = index === focusedIndex;
        const depth = option.depth ?? 0;
        const hasChildren = (option.childCount ?? 0) > 0;
        const isExpanded = expandedIds.has(option.id);
        const paddingLeft = depth * 16 + 22;

        return (
          <li
            key={option.id}
            id={`${optionIdPrefix}-${index}`}
            role="option"
            aria-selected={isSelected}
            className={[
              'vn-dropdown-list__option',
              'vn-dropdown-list__option--tree',
              isSelected ? 'vn-dropdown-list__option--selected' : '',
              isFocused ? 'vn-dropdown-list__option--focused' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{ paddingLeft, display: visibleIds.has(option.id) ? undefined : 'none' }}
            onClick={() => onSelect(option)}
            onMouseDown={(e) => e.preventDefault()}
          >
            {option.icon && option.iconAlign !== 'right' && (
              <span className="vn-dropdown-list__icon vn-dropdown-list__icon--left">{option.icon}</span>
            )}

            {/* Expand / collapse icon — always rendered to keep indentation consistent */}
            <span
              className="vn-dropdown-list__expand-icon"
              style={!hasChildren ? { visibility: 'hidden', pointerEvents: 'none' } : undefined}
              role={hasChildren ? 'button' : undefined}
              tabIndex={hasChildren ? -1 : undefined}
              aria-expanded={hasChildren ? isExpanded : undefined}
              aria-label={hasChildren ? (isExpanded ? `Collapse ${option.label}` : `Expand ${option.label}`) : undefined}
              onClick={(e) => hasChildren && toggleExpand(option.id, e)}
            >
              {isExpanded ? <ArrowDropDownIcon fontSize="small" /> : <ArrowRightIcon fontSize="small" />}
            </span>

            {/* Decorative checkbox for multi-select */}
            {multiple && (
              <input
                type="checkbox"
                readOnly
                checked={isSelected}
                className="vn-dropdown-list__checkbox"
                aria-hidden="true"
                tabIndex={-1}
              />
            )}

            <span className="vn-dropdown-list__label">{option.label}</span>

            {option.icon && option.iconAlign === 'right' && (
              <span className="vn-dropdown-list__icon vn-dropdown-list__icon--right">{option.icon}</span>
            )}

            {!!option.childCount && (
              <span className="vn-dropdown-list__child-count" aria-hidden="true">
                {option.childCount}
              </span>
            )}

            {link && option.isLinkable && (
              <a
                href="#"
                className="vn-dropdown-list__link"
                aria-label={`Open ${option.label}`}
                tabIndex={-1}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  link(option);
                }}
              >
                <OpenInNewIcon fontSize="small" />
              </a>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default VNDropdownOUOptions;

// Signal to VNDropdown that this list slot requires at least one item to remain selected.
(VNDropdownOUOptions as typeof VNDropdownOUOptions & { minSelections: number }).minSelections = 1;
